import React from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation } from 'react-fetching-library';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Stack, Typography, TextField, Button, Modal, Card, CardContent, CardHeader, Checkbox, Alert, LinearProgress, IconButton, Tooltip } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import Iconify from '../../../components/Iconify';
import CloseIcon from '@mui/icons-material/Close';

import OrdersModal from './OrdersModal';
import SendMessageResultModal from './SendMessageResultModal';
import { sendNotificationsAction } from '../../../services/request';
import { setAllowAutoOrderUpdate } from '../../../services/redux-actions';
import RichTextEditor from '../notification/RichTextEditor';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)'
};

const validationSchema = Yup.object({
  email_message: Yup.boolean(),
  sms_message: Yup.boolean(),
  sms_message_any_number: Yup.boolean(),
  any_email_message: Yup.boolean(),
  message: Yup.string().required('To pole nie może być puste'),
  subject: Yup.string().when('email_message', {
    is: true,
    then: Yup.string().required('To pole nie może być puste')
  })
    .when('any_email_message', {
      is: true,
      then: Yup.string().required('To pole nie może być puste')
    }),
  any_numbers: Yup.string().when('sms_message_any_number', {
    is: true,
    then: Yup.string().required('To pole nie może być puste')
  }),
  any_emails: Yup.string().when('any_email_message', {
    is: true,
    then: Yup.string().required('To pole nie może być puste')
  })
});

export default function SendMessage({ idList, setIdList, ordersDataForMessages }) {
  const dispatch = useDispatch();
  const { mutate, error, loading, reset } = useMutation(sendNotificationsAction);
  const { module, seasonYear } = useSelector(state => state.app);
  const [open, setOpen] = React.useState(false);
  const [openOrdersModal, setOpenOrdersModal] = React.useState(false);
  const [openResultModal, setOpenResultModal] = React.useState(false);
  const [result, setResult] = React.useState({});

  const handleOpen = () => {
    dispatch(setAllowAutoOrderUpdate(false));
    setOpen(true);
  };

  const handleClose = () => {
    reset();
    setOpen(false);
    setIdList(ordersDataForMessages.map(order => order.id));
    dispatch(setAllowAutoOrderUpdate(true));
    formik.resetForm();
  };

  const handleOpenOrdersModal = () => setOpenOrdersModal(true);
  const handleCloseOrdersModal = () => setOpenOrdersModal(false);

  const handleOpenResultModal = () => setOpenResultModal(true);
  const handleCloseResultModal = () => setOpenResultModal(false);

  const handleSubmit = async values => {
    const { error: mutateError, payload } = await mutate(values);
    if (!mutateError) {
      setResult(payload);
      handleOpenResultModal();
      formik.resetForm();
    }
  };

  const formik = useFormik({
    initialValues: {
      email_message: false,
      sms_message: false,
      sms_message_any_number: false,
      any_email_message: false,
      message: '',
      subject: '',
      any_numbers: '',
      any_emails: ''
    },
    validationSchema,
    validateOnChange: false,
    onSubmit: values => {
      handleSubmit({
        ...values,
        orders_id: idList,
        module_identifier: module,
        season_year: seasonYear
      });
    }
  });

  function removeHtmlTags(inputString) {
    return inputString.replace(/<[^>]*>|&[^;]+;/g, '');
  }

  return (
    <>
      <Box sx={{ p: 3, width: '100%', display: 'flex', flexDirection: 'row' }}>
        <Button variant="contained" fullWidth endIcon={<EmailIcon />} onClick={handleOpen}>Wyślij</Button>
      </Box>
      <Modal
        open={open}
        onClose={handleClose}
      >
        <Card sx={{ ...style, width: '35vw', overflow: 'auto', maxHeight: '100svh' }}>
          <CardHeader
            title="Wyślij wiadomość"
            action={
              <>
                <Tooltip title="lista zamówień">
                  <IconButton color="primary" onClick={handleOpenOrdersModal}>
                    <Iconify icon="icon-park-outline:transaction-order" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="zamknij">
                  <IconButton color="primary" onClick={handleClose}>
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </>
            }
            sx={{ textAlign: 'center' }}
          />
          <CardContent>
            <form onSubmit={formik.handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
              <Stack direction="column" spacing={4}>
                <Stack direction="row" justifyContent="start">
                  <Stack direction="row" alignItems="center" justifyContent="left" spacing={1}>
                    <Checkbox id="email_message" name="email_message" checked={formik.values.email_message} onChange={formik.handleChange} />
                    <Typography variant="subtitle2">Wiadomość E-mail</Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" justifyContent="left" spacing={1}>
                    <Checkbox id="sms_message" name="sms_message" checked={formik.values.sms_message} onChange={e => {
                      formik.handleChange(e);
                      if (e.target.checked === true) {
                        formik.setFieldValue('message', removeHtmlTags(formik.values.message));
                      }
                    }} />
                    <Typography variant="subtitle2">Wiadomość SMS</Typography>
                  </Stack>
                </Stack>
                <Stack direction="row">
                  <Stack direction="row" alignItems="center" justifyContent="left" spacing={1}>
                    <Checkbox id="any_email_message" name="any_email_message" checked={formik.values.any_email_message} onChange={e => {
                      formik.handleChange(e);
                      if (e.target.checked === true) {
                        setIdList([]);
                      }
                    }} />
                    <Typography variant="subtitle2">Wiadomość do dowolnych adresów e-mail</Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" justifyContent="left" spacing={1}>
                    <Checkbox id="sms_message_any_number" name="sms_message_any_number" checked={formik.values.sms_message_any_number} onChange={e => {
                      formik.handleChange(e);
                      if (e.target.checked === true) {
                        setIdList([]);
                        formik.setFieldValue('message', removeHtmlTags(formik.values.message));
                      }
                    }} />
                    <Typography variant="subtitle2">Wiadomość SMS do dowolnych numerów</Typography>
                  </Stack>
                </Stack>
              </Stack>
              <Box height={20}/>
              {formik.values.any_email_message
                ? <>
                  <TextField
                    id="any_emails"
                    name="any_emails"
                    multiline
                    rows={6}
                    label="Dowolne emaile"
                    value={formik.values.any_emails}
                    onChange={formik.handleChange}
                    error={formik.errors.any_emails && formik.touched.any_emails}
                    helperText={formik.errors.any_emails ? formik.errors.any_emails : 'Każdy email od nowej linii'}
                    sx={{ mb: '20px' }}
                  />
                </>
                : null}
              {formik.values.sms_message_any_number
                ? <>
                  <TextField
                    id="any_numbers"
                    name="any_numbers"
                    multiline
                    rows={6}
                    label="Dowolne numery"
                    value={formik.values.any_numbers}
                    onChange={formik.handleChange}
                    error={formik.errors.any_numbers && formik.touched.any_numbers}
                    helperText={formik.errors.any_numbers ? formik.errors.any_numbers : 'Każdy numer od nowej linii'}
                  />
                </>
                : null}

              {formik.values.email_message || formik.values.any_email_message
                ? <>
                  {formik.values.sms_message_any_number
                    ? <Box height={20}/>
                    : null}
                  <TextField
                    id="subject"
                    name="subject"
                    label="Temat"
                    value={formik.values.subject}
                    onChange={formik.handleChange}
                    error={formik.errors.subject && formik.touched.subject}
                    helperText={formik.errors.subject}
                  />
                </>
                : null}
              <Box height={20}/>
              {formik.values.sms_message || formik.values.sms_message_any_number ? <TextField
                id="message"
                name="message"
                multiline
                rows={8}
                label="Treść wiadomości"
                value={formik.values.message}
                onChange={formik.handleChange}
                error={formik.errors.message && formik.touched.message}
                helperText={formik.errors.message}
                disabled={!formik.values.email_message && !formik.values.sms_message && !formik.values.sms_message_any_number}
              /> : <RichTextEditor
                text={formik.values.message}
                formik={formik}
                fieldName="message"
                label={null}
                placeholder="Treść wiadomości"
                helperText={formik.errors.message}
                error={formik.errors.message && formik.touched.message}
                height="220px"
              />}
              {/* <TextField
                id="message"
                name="message"
                multiline
                rows={8}
                label="Treść wiadomości"
                value={formik.values.message}
                onChange={formik.handleChange}
                error={formik.errors.message && formik.touched.message}
                helperText={formik.errors.message}
                disabled={!formik.values.email_message && !formik.values.sms_message && !formik.values.sms_message_any_number}
              /> */}
              { error
                ? <>
                  <Box height={40}/>
                  <Alert variant="outlined" severity="error">Nie udało się wysłać wiadomości</Alert>
                </>
                : null}
              <Box height={40}/>
              { loading
                ? <>
                  <LinearProgress color="success" />
                </>
                : <Button disabled={!formik.values.email_message && !formik.values.sms_message && !formik.values.sms_message_any_number && !formik.values.any_email_message} type="submit" variant="contained">
                  Wyślij
                </Button>}
            </form>
          </CardContent>
        </Card>
      </Modal>
      <OrdersModal
        open={openOrdersModal}
        handleClose={handleCloseOrdersModal}
        idList={idList}
        setIdList={setIdList}
        ordersDataForMessages={ordersDataForMessages}
      />
      <SendMessageResultModal open={openResultModal} handleClose={handleCloseResultModal} result={result} />
    </>
  );
}
